import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Icon,
    ModalDialog,
    MultilineInput,
    Tabs,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddEditValueModalSkeleton } from './AddEditValueModalSkeleton';
import { createCompanyValueSchema, DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from './validation';
import ValueQuestionsTab from './ValueQuestionsTab';
import ValueTranslationsTab from './ValueTranslationsTab';
import { buildGradientFromColor } from '../../utils/colorUtils';
import { resolveCompanyValueIconBackground } from '../../utils/companyValueIconBackground';
import { getIconSvg } from '../../utils/iconSvgs';
import ColorPicker from '../ColorPicker/ColorPicker';
import IconPicker from '../IconPicker/IconPicker';
import { NumberIcon } from './components/NumberIcon';

import type { CompanyValueFormData } from './validation';
import type { CompanyValue } from '@/types/company-values';
import type { Control } from 'react-hook-form';

import {
    useGetCompanyValueQuestions,
    useUpdateCompanyValueQuestionTranslations,
} from '@/api/company-value-questions';
import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import { VISION_MODAL } from '@/components/VisionValues/visionModalChrome';
import { useSelectedAppLanguage } from '@/hooks';
import { getTextByLanguage } from '@/utils/localization';
import { validationMessageKey } from '@/utils/validationMessage';

interface AddEditValueModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (
        value: Omit<CompanyValue, 'id' | 'createdAt' | 'updatedAt' | 'companyId' | 'sortOrder'>,
    ) => Promise<void>;
    value?: CompanyValue;
    existingValues?: CompanyValue[];
}

type Tab = 'details' | 'questions' | 'translations';

// One shared empty list: a fresh `[]` default on every render would re-run the name re-validation
// effect below on each render.
const NO_EXISTING_VALUES: CompanyValue[] = [];

interface CharacterCountProps {
    control: Control<CompanyValueFormData>;
    fieldName: 'name' | 'description';
    maxLength: number;
}

function CharacterCount({ control, fieldName, maxLength }: CharacterCountProps) {
    const value = useWatch({ control, name: fieldName });
    const currentLength = (value || '').length;
    const isAtLimit = currentLength >= maxLength;

    return (
        <p
            className={`text-caption text-right ${isAtLimit ? 'font-semibold text-red-500' : 'text-gray-400'}`}
        >
            {currentLength}/{maxLength}
        </p>
    );
}

export default function AddEditValueModal({
    isOpen,
    onClose,
    onSave,
    value,
    existingValues = NO_EXISTING_VALUES,
}: AddEditValueModalProps) {
    const { t } = useTranslation();
    const company = useAtomValue(companyAtom);
    const user = useAtomValue(userInstanceAtom);
    const configuredLanguages = company?.company?.availableLanguages || user?.surveyLanguages || [];
    const currentLanguage = useSelectedAppLanguage();

    const [activeTab, setActiveTab] = useState<Tab>('details');
    const [selectedColor, setSelectedColor] = useState<string | null>(null);
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
    const [initialColor, setInitialColor] = useState<string | null>(null);
    const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
    const [translationsValid, setTranslationsValid] = useState(true);

    // Fetch questions for translations tab when editing
    const { data: questionsData = [], isLoading: isLoadingQuestions } = useGetCompanyValueQuestions(
        value?.id || '',
    );
    const updateCompanyValueQuestionTranslations = useUpdateCompanyValueQuestionTranslations();

    const questions = questionsData.filter((q) => q.id !== undefined);

    // Dynamic schema that includes uniqueness validation
    const companyValueSchema = createCompanyValueSchema(existingValues, value?.id, currentLanguage);

    const methods = useForm<CompanyValueFormData>({
        resolver: zodResolver(companyValueSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            icon: '',
            iconBackground: '',
        },
    });

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        setError,
        watch,
        formState: { errors },
        control,
    } = methods;

    const isEdit = !!value;

    useEffect(() => {
        if (isOpen) {
            if (value) {
                // Extract current language value only — no fallback to other languages
                const defaultName = value.name?.[currentLanguage] || '';
                const defaultDescription = value.description?.[currentLanguage] || '';

                reset({
                    name: defaultName,
                    description: defaultDescription,
                    icon: value.icon || '',
                    iconBackground: value.iconBackground || '',
                });
                // Extract hex color from gradient if present
                const hexMatch = value.iconBackground?.match(/#([0-9a-fA-F]{6})/);
                const extractedColor = hexMatch ? `#${hexMatch[1]}` : null;
                setSelectedColor(extractedColor);
                setInitialColor(extractedColor);
                setSelectedIcon(value.icon || null);

                // Set translations state (excluding current language)
                const translationsCopy: Record<string, Record<string, string>> = {};
                if (value.name) {
                    Object.entries(value.name).forEach(([lang, text]) => {
                        if (lang !== currentLanguage) {
                            if (!translationsCopy[lang]) translationsCopy[lang] = {};
                            translationsCopy[lang].name = text;
                        }
                    });
                }
                if (value.description) {
                    Object.entries(value.description).forEach(([lang, text]) => {
                        if (lang !== currentLanguage) {
                            if (!translationsCopy[lang]) translationsCopy[lang] = {};
                            translationsCopy[lang].description = text;
                        }
                    });
                }

                setTranslations(translationsCopy);
            } else {
                reset({
                    name: '',
                    description: '',
                    icon: '',
                    iconBackground: '',
                });
                setSelectedColor(null);
                setInitialColor(null);
                setSelectedIcon(null);
                setTranslations({});
            }
            setActiveTab('details');
        }
    }, [isOpen, value, reset, currentLanguage]);

    // Load question translations separately when editing (only when value.id exists)
    useEffect(() => {
        if (isOpen && value && questionsData.length > 0) {
            setTranslations((prevTranslations) => {
                const translationsCopy = { ...prevTranslations };
                questionsData.forEach((question) => {
                    // Load label translations
                    if (question.label) {
                        Object.entries(question.label).forEach(([lang, text]) => {
                            if (lang !== currentLanguage) {
                                if (!translationsCopy[lang]) translationsCopy[lang] = {};
                                translationsCopy[lang][`question_${question.id}`] = text;
                            }
                        });
                    }

                    // Load option translations
                    if (question.reviewQuestionOptions) {
                        question.reviewQuestionOptions.forEach((option) => {
                            if (option.label) {
                                Object.entries(option.label).forEach(([lang, text]) => {
                                    if (lang !== currentLanguage) {
                                        if (!translationsCopy[lang]) translationsCopy[lang] = {};
                                        translationsCopy[lang][`option_${option.id}`] = text;
                                    }
                                });
                            }
                        });
                    }
                });
                return translationsCopy;
            });
        }
    }, [isOpen, value, questionsData, currentLanguage]);

    // Re-checks the name's uniqueness when the list it is compared against changes, but only once
    // the user has worked on the field: validating on opening showed "name is required" on a
    // value nobody had started typing yet. Typing and saving validate on their own.
    useEffect(() => {
        if (!isOpen) return;

        const { isDirty, isTouched } = methods.getFieldState('name');
        if (isDirty || isTouched) {
            methods.trigger('name');
        }
    }, [existingValues, value?.id, currentLanguage, isOpen, methods]);

    const handleSave = async (data: CompanyValueFormData) => {
        try {
            // Only set a background when the user explicitly picked or changed a color.
            // Preserve an existing background while editing; otherwise leave it unset.
            const colorChanged = selectedColor !== initialColor;

            let iconBackground: string | undefined;
            if (selectedColor && colorChanged) {
                iconBackground = buildGradientFromColor(selectedColor);
            } else if (!colorChanged && value?.iconBackground) {
                iconBackground = value.iconBackground.trim();
            }

            // Build name dictionary with current language and additional translations
            const nameDict: Record<string, string> = {
                [currentLanguage]: data.name.trim(),
            };
            Object.entries(translations).forEach(([lang, trans]) => {
                if (trans.name) {
                    nameDict[lang] = trans.name.trim();
                }
            });

            // Build description dictionary with current language and additional translations
            const descriptionDict: Record<string, string> = {};
            const trimmedDescription = data.description?.trim() || '';
            if (trimmedDescription) {
                descriptionDict[currentLanguage] = trimmedDescription;
            }
            Object.entries(translations).forEach(([lang, trans]) => {
                if (trans.description) {
                    descriptionDict[lang] = trans.description.trim();
                }
            });

            await onSave({
                name: nameDict,
                description: descriptionDict,
                icon: selectedIcon || data.icon?.trim() || '',
                iconBackground,
                status: value?.status || 'Active',
            });

            // Save question translations separately
            if (value?.id) {
                for (const question of questions) {
                    const questionTranslationUpdates: Record<string, string> = {};
                    const optionTranslationUpdates: Record<string, Record<string, string>> = {};

                    Object.entries(translations).forEach(([lang, trans]) => {
                        // Question label translations
                        const questionTranslation = trans[`question_${question.id}`];
                        if (questionTranslation) {
                            questionTranslationUpdates[lang] = questionTranslation.trim();
                        }

                        // Option translations
                        if (question.reviewQuestionOptions) {
                            question.reviewQuestionOptions.forEach((option) => {
                                const optionTranslation = trans[`option_${option.id}`];
                                if (optionTranslation) {
                                    if (!optionTranslationUpdates[option.id]) {
                                        optionTranslationUpdates[option.id] = {};
                                    }
                                    optionTranslationUpdates[option.id][lang] =
                                        optionTranslation.trim();
                                }
                            });
                        }
                    });

                    if (
                        Object.keys(questionTranslationUpdates).length > 0 ||
                        Object.keys(optionTranslationUpdates).length > 0
                    ) {
                        const existingQuestion = questionsData.find((q) => q.id === question.id);
                        if (existingQuestion) {
                            const updatedLabel = {
                                ...existingQuestion.label,
                                ...questionTranslationUpdates,
                            };

                            const updatedOptions = existingQuestion.reviewQuestionOptions?.map(
                                (opt) => ({
                                    ...opt,
                                    label: {
                                        ...opt.label,
                                        ...(optionTranslationUpdates[opt.id] || {}),
                                    },
                                }),
                            );

                            await updateCompanyValueQuestionTranslations.mutateAsync({
                                id: question.id!,
                                companyValueId: value.id,
                                request: {
                                    label: updatedLabel,
                                    reviewQuestionOptions: (updatedOptions ?? []).map((opt) => ({
                                        id: opt.id,
                                        label: opt.label,
                                    })),
                                },
                            });
                        }
                    }
                }
            }

            reset();
            setSelectedColor(null);
            setInitialColor(null);
            setSelectedIcon(null);
            setTranslations({});
            setActiveTab('details');
            onClose();
        } catch (err) {
            const msg = err instanceof Error ? err.message : '';
            if (msg.toLowerCase().includes('already exists')) {
                setError('name', { message: 'visionValues.nameDuplicateError' });
            }
        }
    };

    const handleIconChange = (iconId: string | null) => {
        setSelectedIcon(iconId);
        setValue('icon', iconId || '');
    };

    const handleColorReset = () => {
        setValue('iconBackground', '');
    };

    const handleClose = () => {
        reset();
        setSelectedColor(null);
        setSelectedIcon(null);
        setActiveTab('details');
        onClose();
    };

    const handleTabChange = (key: string) => {
        // Prevent switching to questions tab when not in edit mode
        if (key === 'questions' && !isEdit) {
            return;
        }
        setActiveTab(key as Tab);
    };

    const previewGradient = selectedColor
        ? buildGradientFromColor(selectedColor)
        : resolveCompanyValueIconBackground(selectedIcon || 'heart', watch('iconBackground'));

    const tabItems = [
        {
            key: 'details',
            label: (
                <div className={'text-body-sm flex items-center gap-2'}>
                    <Icon
                        name={'lucide-image'}
                        size={'md'}
                        colorClassName={activeTab === 'details' ? 'text-teal-500' : 'text-gray-500'}
                    />
                    {t('visionValues.detailsTab')}
                </div>
            ),
        },
        {
            key: 'questions',
            label: (
                <div
                    className={'text-body-sm flex items-center gap-2'}
                    title={!isEdit ? t('visionValues.saveValueBeforeAddingQuestions') : ''}
                >
                    <Icon
                        name={'lucide-clipboard-list'}
                        size={'md'}
                        colorClassName={
                            !isEdit
                                ? 'text-gray-300'
                                : activeTab === 'questions'
                                  ? 'text-teal-500'
                                  : 'text-gray-500'
                        }
                    />
                    <span className={!isEdit ? 'text-gray-300' : ''}>
                        {t('visionValues.evaluationQuestions')}
                    </span>
                    <NumberIcon number={questions.length} />
                </div>
            ),
        },
        {
            key: 'translations',
            label: (
                <div className={'text-body-sm flex items-center gap-2'}>
                    <Icon
                        name={'lucide-languages'}
                        size={'sm'}
                        colorClassName={
                            activeTab === 'translations' ? 'text-teal-500' : 'text-gray-500'
                        }
                    />
                    {t('visionValues.translationsTab')}
                </div>
            ),
        },
    ];

    return (
        // `ModalDialog` carries the dialog role, its name, Escape and the focus handover; the width
        // stays the one this window had.
        <ModalDialog
            isOpen={isOpen}
            onClose={handleClose}
            closeAriaLabel={t('common.close')}
            title={isEdit ? t('visionValues.editValue') : t('visionValues.addValue')}
            subtitle={t('visionValues.modalSubtitle')}
            titleIcon={
                <div
                    className={
                        'flex h-[70px] w-[70px] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl shadow-lg'
                    }
                    style={{ background: previewGradient }}
                >
                    {getIconSvg(selectedIcon || 'heart', 36)}
                </div>
            }
            headerExtra={
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={tabItems}
                    className={VISION_MODAL.tabsRuleOff}
                />
            }
            footer={
                // Hidden on the Questions tab: its questions have their own Update button.
                activeTab === 'questions' ? null : (
                    <>
                        <ButtonSecondary
                            onClick={handleClose}
                            fitContent
                            size={'md'}
                            dataTestId={'value-cancel'}
                        >
                            {t('visionValues.cancel')}
                        </ButtonSecondary>

                        <ButtonGradientPrimary
                            onClick={handleSubmit(handleSave)}
                            size={'md'}
                            disabled={
                                !watch('name')?.trim() ||
                                !translationsValid ||
                                Object.keys(errors).length > 0
                            }
                            fitContent
                            dataTestId={'value-save'}
                        >
                            {isEdit ? t('visionValues.save') : t('visionValues.addValue')}
                        </ButtonGradientPrimary>
                    </>
                )
            }
            className={VISION_MODAL.panelWidth}
            dataTestId={'add-edit-value-modal'}
            chrome={'banded'}
            radius={'2xl'}
            shadow={'2xl'}
            overlay={'medium'}
            backdropBlur
        >
            {isLoadingQuestions ? (
                <div className={'flex flex-col gap-6'}>
                    <AddEditValueModalSkeleton />
                </div>
            ) : (
                <>
                    {activeTab === 'details' && (
                        <div className={'flex flex-col gap-6'}>
                            {/* Name */}
                            <div>
                                <div className={'mb-2 flex flex-col gap-2'}>
                                    <label
                                        htmlFor={'name'}
                                        className={'text-navy text-md font-medium'}
                                    >
                                        {t('visionValues.nameLabel')}{' '}
                                        <span className={'text-red-500'}>*</span>
                                    </label>
                                    <Controller
                                        name={'name'}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextInput
                                                id={'name'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                placeholder={t('visionValues.namePlaceholder')}
                                                maxLength={NAME_MAX_LENGTH}
                                                size={VISION_MODAL.fieldSize}
                                                textSize={VISION_MODAL.fieldTextSize}
                                                variant={'white'}
                                                bordered
                                                className={VISION_MODAL.fieldBorder}
                                                error={!!fieldState.error}
                                                errorMessage={
                                                    fieldState.error?.message
                                                        ? t(
                                                              validationMessageKey(
                                                                  fieldState.error.message,
                                                              ),
                                                              { max: NAME_MAX_LENGTH },
                                                          )
                                                        : undefined
                                                }
                                                dataTestId={'value-name-input'}
                                            />
                                        )}
                                    />

                                    <CharacterCount
                                        control={control}
                                        fieldName={'name'}
                                        maxLength={NAME_MAX_LENGTH}
                                    />
                                </div>

                                {/* Description */}
                                <div className={'flex flex-col gap-2'}>
                                    <label
                                        htmlFor={'description'}
                                        className={'text-navy text-md font-medium'}
                                    >
                                        {t('visionValues.descriptionLabel')}
                                    </label>
                                    <Controller
                                        name={'description'}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <MultilineInput
                                                id={'description'}
                                                value={field.value ?? ''}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                rows={3}
                                                placeholder={t(
                                                    'visionValues.descriptionPlaceholder',
                                                )}
                                                maxLength={DESCRIPTION_MAX_LENGTH}
                                                variant={'white'}
                                                className={VISION_MODAL.fieldBorder}
                                                error={!!fieldState.error}
                                                errorMessage={
                                                    fieldState.error?.message
                                                        ? t(
                                                              validationMessageKey(
                                                                  fieldState.error.message,
                                                              ),
                                                              { max: DESCRIPTION_MAX_LENGTH },
                                                          )
                                                        : undefined
                                                }
                                                dataTestId={'value-description-input'}
                                            />
                                        )}
                                    />

                                    <CharacterCount
                                        control={control}
                                        fieldName={'description'}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                    />
                                </div>
                            </div>

                            {/* Icon */}
                            <IconPicker
                                selectedIcon={selectedIcon}
                                onIconChange={handleIconChange}
                            />

                            {/* Hidden icon input for tests */}
                            <input
                                type={'hidden'}
                                {...register('icon')}
                            />

                            {/* Icon Background */}
                            <ColorPicker
                                selectedColor={selectedColor}
                                onColorChange={setSelectedColor}
                                defaultColor={'#0DD3C3'}
                                onReset={handleColorReset}
                            />
                        </div>
                    )}

                    {activeTab === 'questions' && (
                        <ValueQuestionsTab
                            companyValueId={value?.id || ''}
                            companyValueName={getTextByLanguage(value?.name, currentLanguage)}
                        />
                    )}

                    {activeTab === 'translations' && (
                        <ValueTranslationsTab
                            valueId={value?.id || null}
                            questions={questions}
                            configuredLanguages={configuredLanguages}
                            currentLanguage={currentLanguage}
                            initialTranslations={translations}
                            onTranslationsChange={setTranslations}
                            onValidationChange={setTranslationsValid}
                        />
                    )}
                </>
            )}
        </ModalDialog>
    );
}
